/**
 * SectionIntegrations — de qué servicios externos depende la plataforma.
 *
 * La sección existe para responder con honestidad a una pregunta incómoda:
 * "¿qué de todo esto funciona ya?". Por eso el dato `real` manda sobre
 * cualquier otra cosa y parte la pantalla en dos grupos que nunca se mezclan:
 *
 *   real: true  → servicios enlazados de verdad, en uso hoy por la plataforma
 *   real: false → capacidades del producto hostelero, todavía sin conectar
 *
 * Decisiones no obvias:
 *
 *   1. Aquí no se conecta nada. No hay formularios, ni botones de conectar, ni
 *      pantallas intermedias: enlazar un servicio externo es contratarlo y
 *      configurarlo fuera, no rellenar un campo. Las tarjetas son informativas.
 *   2. Ni una clave, ni un token, ni una dirección técnica, ni un identificador
 *      de cuenta. Las credenciales viven fuera de la interfaz y esta pantalla
 *      no las muestra ni las pide (ver PRODUCT_V2_SPEC §13.1).
 *   3. `statusFor` degrada el estado: una capacidad futura no puede lucir
 *      "Conectado" aunque el dato lo dijera. Es una red de seguridad para que
 *      un cambio en el dataset no convierta esta pantalla en una promesa.
 *   4. El channel manager es el caso más delicado. Se muestra en el grupo
 *      futuro, sin proveedor elegido y con el aviso de que no hay conexión:
 *      evaluar una opción no es haberla contratado.
 */
import React from "react";
import {
  ShareNetwork, WhatsappLogo, Envelope, DeviceMobile, Robot, TrendUp,
  CreditCard, PlugsConnected, Lock, ShieldCheck,
} from "@phosphor-icons/react";
import {
  Block, Card, Note, Advanced, StatusChip,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2 } from "@/lib/settingsV2Store";
import { INTEGRATION_STATUS } from "@/data/settingsV2Demo";

/** Icono por integración; cualquier añadido futuro cae en el genérico. */
const ICONS = {
  channel_manager: ShareNetwork,
  whatsapp: WhatsappLogo,
  email: Envelope,
  push: DeviceMobile,
  ai: Robot,
  pricing: TrendUp,
  payments: CreditCard,
};

/** Qué significa cada estado, dicho en las palabras del cliente. */
const STATUS_MEANING = {
  connected: "El servicio está enlazado y la plataforma lo usa a diario.",
  needs_setup:
    "La capacidad forma parte del producto, pero falta decidir y contratar el servicio que la hará funcionar.",
  coming_later:
    "Todavía no interviene en el trabajo diario. Se conectará cuando la operación lo pida.",
};

/**
 * Estado que se muestra en la tarjeta. Regla de honestidad: si la integración
 * no es real, nunca se muestra como conectada.
 */
function statusFor(integration) {
  if (!integration.real && integration.status === "connected") {
    return INTEGRATION_STATUS.coming_later;
  }
  return INTEGRATION_STATUS[integration.status] || INTEGRATION_STATUS.coming_later;
}

export default function SectionIntegrations() {
  const { integrations } = useSettingsV2();

  const active = integrations.filter((i) => i.real);
  const future = integrations.filter((i) => !i.real);

  return (
    <div data-testid="section-integrations">
      <Block
        title="Integraciones activas hoy"
        description="Servicios externos que la plataforma ya usa. Están enlazados y funcionando: si alguno se cayera, se notaría hoy mismo en el trabajo del equipo."
        testId="integrations-active"
        action={
          <span
            className="text-[11px] text-stone-400 dark:text-stone-500 tabular-nums"
            data-testid="integrations-active-count"
          >
            {active.length} en uso
          </span>
        }
      >
        <Note testId="integrations-intro">
          Una integración es un servicio de fuera del que la plataforma depende para algo
          concreto: enviar un mensaje, cobrar, publicar disponibilidad. Aquí solo se consulta en
          qué punto está cada uno.
        </Note>

        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
          data-testid="integrations-list-active"
        >
          {active.map((integration) => (
            <IntegrationCard key={integration.key} integration={integration} />
          ))}
        </div>
      </Block>

      <Block
        title="Capacidades del producto hostelero"
        description="Lo que el producto conectará cuando la operación esté en marcha. Hoy ninguna de estas está enlazada, y en ninguna hay proveedor elegido ni contratado."
        testId="integrations-future"
        action={
          <span
            className="text-[11px] text-stone-400 dark:text-stone-500 tabular-nums"
            data-testid="integrations-future-count"
          >
            {future.length} por conectar
          </span>
        }
      >
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
          data-testid="integrations-list-future"
        >
          {future.map((integration) => (
            <IntegrationCard key={integration.key} integration={integration} />
          ))}
        </div>
      </Block>

      <Advanced label="Qué quiere decir cada estado" testId="integrations-status-legend">
        <Card testId="integrations-legend-card">
          <div className="space-y-4">
            {Object.entries(INTEGRATION_STATUS).map(([key, status]) => (
              <div
                key={key}
                className="flex flex-col sm:flex-row sm:items-baseline gap-1.5 sm:gap-3"
                data-testid={`integrations-legend-${key}`}
              >
                <span className="shrink-0">
                  <StatusChip tone={status.tone}>{status.label}</StatusChip>
                </span>
                <span className="text-xs text-stone-500 dark:text-stone-400 max-w-xl">
                  {STATUS_MEANING[key]}
                </span>
              </div>
            ))}
          </div>

          <div
            className="mt-5 pt-4 border-t border-stone-100 dark:border-stone-800 flex items-start gap-2"
            data-testid="integrations-credentials"
          >
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-stone-400" />
            <p className="text-xs text-stone-500 dark:text-stone-400 max-w-xl">
              Las contraseñas y claves de cada servicio se guardan fuera de la interfaz. No se
              muestran en esta pantalla ni en ninguna otra, y tampoco se piden aquí.
            </p>
          </div>
        </Card>
      </Advanced>

      <Note testId="integrations-preview-note">
        Desde esta vista previa no se puede conectar ninguna integración futura. Enlazar un
        servicio externo se decide y se contrata fuera del producto, y hasta entonces esta
        pantalla se limita a contar en qué punto está cada uno.
      </Note>
    </div>
  );
}

/** Tarjeta informativa: para qué sirve, en qué estado está y quién lo presta. */
function IntegrationCard({ integration }) {
  const Icon = ICONS[integration.key] || PlugsConnected;
  const status = statusFor(integration);

  return (
    <Card className="flex flex-col" testId={`integrations-card-${integration.key}`}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
          <Icon size={18} className="text-stone-600 dark:text-stone-300" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100 truncate">
            {integration.label}
          </div>
          <div className="text-[11px] text-stone-400 dark:text-stone-500">
            {integration.real ? "En uso hoy" : "Todavía sin conectar"}
          </div>
        </div>
      </div>

      <p
        className="text-xs text-stone-500 dark:text-stone-400 mt-3"
        data-testid={`integrations-purpose-${integration.key}`}
      >
        {integration.purpose}
      </p>

      <p
        className="text-[11px] text-stone-400 dark:text-stone-500 mt-2 flex-1"
        data-testid={`integrations-provider-${integration.key}`}
      >
        {integration.provider_note}
      </p>

      <div className="mt-4 pt-3 border-t border-stone-100 dark:border-stone-800 flex items-center gap-x-3 gap-y-2 flex-wrap">
        <StatusChip tone={status.tone} testId={`integrations-status-${integration.key}`}>
          {status.label}
        </StatusChip>
        {!integration.real && (
          <span
            className="inline-flex items-center gap-1 text-[11px] text-stone-400 dark:text-stone-500"
            data-testid={`integrations-locked-${integration.key}`}
          >
            <Lock size={11} weight="fill" className="shrink-0" />
            Sin conexión ni proveedor
          </span>
        )}
      </div>
    </Card>
  );
}
